import { useState, useEffect } from "react";
import type { ProductInfoCreative } from "@/feature/ads/mock/productsInfoCreative.mock";
import type { AdStyle, SelectedStyleConfig } from "./adStudio/types";
import { MOCK_STYLES } from "./adStudio/stylesData";
import { templatesService } from "@/feature/ads/cretives/services/templates.service";
import { AdStudioHeader } from "./adStudio/AdStudioHeader";
import { AdStudioGallery, MAX_SELECTION, MIN_SELECTION } from "./adStudio/AdStudioGallery";
import { AdStudioSidebar } from "./adStudio/AdStudioSidebar";
import { dictionary, Language } from "@/utils/dictionary";
import { toBase64 } from "@/utils/utils";
import { uploadImagesToS3 } from "@/services/s3/s3Service";
import useCreativeGenerationStore from "@/store/creativeGenerationStore";
import toast from "react-hot-toast";
import { adsSpyService } from "@/feature/ads/spy/services/adsSpy.service";
import type { SpyAdReference } from "../CreateAdWithIAModal";

const FOLDER_ADS_CUSTOM = "ads-creatives-custom";

interface Step2AdStudioProps {
  product: ProductInfoCreative;
  websiteId: string;
  onClose: () => void;
  language: Language;
  initialReference?: SpyAdReference;
  onGenerated?: () => void;
}

export function Step2AdStudio({
  product,
  websiteId,
  onClose,
  language,
  initialReference,
  onGenerated,
}: Step2AdStudioProps) {
  const [templates, setTemplates] = useState<AdStyle[]>(MOCK_STYLES);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [selectedStylesConfig, setSelectedStylesConfig] = useState<Map<string, SelectedStyleConfig>>(new Map());
  const [expandedConfigs, setExpandedConfigs] = useState<Set<string>>(new Set());
  const [customReferencePreview, setCustomReferencePreview] = useState<string | undefined>();
  const [customUploadLoading, setCustomUploadLoading] = useState(false);
  const startGeneration = useCreativeGenerationStore((s) => s.startGeneration);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingTemplates(true);
    templatesService.getTemplates().then((data) => {
      if (!cancelled) {
        setTemplates(data);
        setIsLoadingTemplates(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Pre-populate custom reference from Ads Spy — re-upload to S3 via backend (avoids CORS)
  useEffect(() => {
    if (!initialReference?.image_url) return;

    setCustomReferencePreview(initialReference.image_url);
    setCustomUploadLoading(true);

    adsSpyService.reuploadImage(initialReference.image_url)
      .then((result) => {
        const s3Url = result.s3_url;
        setCustomReferencePreview(s3Url);
        setSelectedStylesConfig((prev) => {
          const newMap = new Map(prev);
          newMap.set("custom-reference", {
            style: {
              id: "custom-reference",
              coverImage: s3Url,
              name: dictionary["personalized"]?.[language] || "Custom",
              category: "custom",
              image_url: s3Url,
            },
            format: "1:1",
            colors: "auto",
            instructions: "",
            quantity: 1,
            primaryColor: "#4368E5",
            backgroundColor: "#FFFFFF",
          });
          return newMap;
        });
        setExpandedConfigs((e) => new Set(e).add("custom-reference"));
      })
      .catch((err) => {
        console.error("[Step2AdStudio] Failed to re-upload spy image:", err);
        toast.error(dictionary["could-not-load-reference-image"]?.[language] || "Could not load reference image. Upload it manually.");
        setCustomReferencePreview(undefined);
        setSelectedStylesConfig((prev) => {
          const newMap = new Map(prev);
          newMap.delete("custom-reference");
          return newMap;
        });
        setExpandedConfigs((e) => {
          const next = new Set(e);
          next.delete("custom-reference");
          return next;
        });
      })
      .finally(() => {
        setCustomUploadLoading(false);
      });
  }, [initialReference]);

  const MAX_SELECTION = 20;

  const handleToggleStyle = (id: string) => {
    setSelectedStylesConfig((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(id)) {
        newMap.delete(id);
        setExpandedConfigs((e) => {
          const next = new Set(e);
          next.delete(id);
          return next;
        });
        return newMap;
      }
      // Block adding if max reached
      if (newMap.size >= MAX_SELECTION) return prev;
      if (id === "custom-reference") {
        newMap.set(id, {
          style: {
            id: "custom-reference",
            coverImage: customReferencePreview ?? "",
            name: dictionary["personalized"]?.[language] || "Custom",
            category: "custom",
            image_url: customReferencePreview ?? "",
          },
          format: "1:1",
          colors: "auto",
          instructions: "",
          quantity: 1,
          primaryColor: "#4368E5",
          backgroundColor: "#FFFFFF",
        });
        setExpandedConfigs((e) => new Set(e).add(id));
        return newMap;
      }
      const style = templates.find((s) => s.id === id);
      if (!style || !style.image_url) return prev;
      newMap.set(id, {
        style: {
          ...style,
          image_url: style.image_url,
        },
        format: "1:1",
        colors: "auto",
        instructions: "",
        quantity: 1,
        primaryColor: "#4368E5",
        backgroundColor: "#FFFFFF",
      });
      setExpandedConfigs((e) => new Set(e).add(id));
      return newMap;
    });
  };

  const handleCustomFileSelect = async (file: File) => {
    setCustomUploadLoading(true);
    try {
      const base64File = await toBase64(file);
      const filename = `${Date.now()}-${file.name}`;
      const response = await uploadImagesToS3({
        file: base64File,
        folder: FOLDER_ADS_CUSTOM,
        filename,
      });
      const s3Url = response?.s3_url;
      if (!s3Url) {
        throw new Error("No se recibió URL en la respuesta de S3");
      }
      setCustomReferencePreview(s3Url);
      setSelectedStylesConfig((prev) => {
        const newMap = new Map(prev);
        newMap.set("custom-reference", {
          style: {
            id: "custom-reference",
            coverImage: s3Url,
            name: dictionary["personalized"]?.[language] || "Custom",
            category: "custom",
            image_url: s3Url,
          },
          format: "1:1",
          colors: "auto",
          instructions: "",
          quantity: 1,
          primaryColor: "#4368E5",
          backgroundColor: "#FFFFFF",
        });
        setExpandedConfigs((e) => new Set(e).add("custom-reference"));
        return newMap;
      });
      toast.success(dictionary["image-uploaded-successfully"]?.[language] || "Image uploaded successfully");
    } catch (err) {
      console.error("Error al subir imagen custom:", err);
      toast.error(dictionary["error-uploading-image"]?.[language] || "Error uploading image. Try again.");
    } finally {
      setCustomUploadLoading(false);
    }
  };

  const handleCustomFileRemove = () => {
    setCustomReferencePreview(undefined);
    setSelectedStylesConfig((prev) => {
      const next = new Map(prev);
      next.delete("custom-reference");
      return next;
    });
    setExpandedConfigs((e) => {
      const next = new Set(e);
      next.delete("custom-reference");
      return next;
    });
  };

  const handleRemoveStyle = (styleId: string) => {
    if (styleId === "custom-reference") {
      setCustomReferencePreview(undefined);
    }
    setSelectedStylesConfig((prev) => {
      const newMap = new Map(prev);
      newMap.delete(styleId);
      return newMap;
    });
    setExpandedConfigs((expanded) => {
      const newExpanded = new Set(expanded);
      newExpanded.delete(styleId);
      return newExpanded;
    });
  };

  const handleToggleConfig = (styleId: string) => {
    setExpandedConfigs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(styleId)) {
        newSet.delete(styleId);
      } else {
        newSet.add(styleId);
      }
      return newSet;
    });
  };

  const handleUpdateConfig = (styleId: string, updates: Partial<SelectedStyleConfig>) => {
    setSelectedStylesConfig((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(styleId);
      if (current) {
        newMap.set(styleId, { ...current, ...updates });
      }
      return newMap;
    });
  };

  const selectedStyles = Array.from(selectedStylesConfig.values());
  const selectedStyleIds = Array.from(selectedStylesConfig.keys());
  const canGenerate = selectedStyles.length >= 1 && !customUploadLoading;

  const selectedStylesForSidebar =
    customUploadLoading
      ? selectedStyles.filter((c) => c.style.id !== "custom-reference")
      : selectedStyles;

  const handleGenerate = async () => {
    const jobs = selectedStyles.map((config) => {
      const colorMode: "auto" | "custom" = config.colors === "auto" ? "auto" : "custom";
      const isCompetitorRef = config.style.id === "custom-reference" && !!initialReference;
      return {
        product_name: product.name,
        product_image: product.image_url ?? "",
        product_description: product.description ?? "",
        user_prompt: config.instructions,
        image_template: config.style.image_url,
        format: config.format,
        template_id: config.style.name_id ?? (isCompetitorRef ? "custom-reference" : ""),
        color_mode: colorMode as "auto" | "custom",
        primary_color: colorMode === "auto" ? null : (config.primaryColor ?? null),
        background_color: colorMode === "auto" ? null : (config.backgroundColor ?? null),
        reference_type: isCompetitorRef ? "competitor_ad" : "template",
        config: {
          language: language,
        },
      };
    });

    // Dispatch to backend async endpoint (credits validated server-side)
    const started = await startGeneration(jobs, {
      productId: product.product_id,
      websiteId: websiteId,
    });

    // Only close modal and navigate if generation actually started
    if (!started) return;

    onGenerated?.();
    onClose();
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
      <AdStudioHeader product={product} onClose={onClose} language={language} />

      {selectedStyleIds.length >= MAX_SELECTION && (
        <div className="bg-amber-50 border-b border-amber-200 px-8 py-3 flex items-center justify-center shrink-0">
          <p className="text-sm font-medium text-amber-700">
            {dictionary["max-selection-reached"]?.[language as Language] || "Has alcanzado el máximo de 20 plantillas. Genera estas antes de solicitar más."}
          </p>
        </div>
      )}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-[60%] min-w-0 min-h-0 overflow-hidden flex-none">
          <AdStudioGallery
            templates={templates}
            isLoadingTemplates={isLoadingTemplates}
            selectedStyles={selectedStyleIds}
            onToggleStyle={handleToggleStyle}
            maxSelection={MAX_SELECTION}
            customReferencePreview={customReferencePreview}
            onCustomFileSelect={handleCustomFileSelect}
            onCustomFileRemove={handleCustomFileRemove}
            isCustomUploadLoading={customUploadLoading}
            product={{
              name: product.name,
              image: product.image_url ?? "",
              description: product.description,
            }}
            language={language}
          />
        </div>

        <AdStudioSidebar
          selectedStyles={selectedStylesForSidebar}
          expandedConfigs={expandedConfigs}
          onRemoveStyle={handleRemoveStyle}
          onToggleConfig={handleToggleConfig}
          onUpdateConfig={handleUpdateConfig}
          maxSelection={MAX_SELECTION}
          language={language}
          canGenerate={canGenerate}
          onGenerate={handleGenerate}
        />
      </div>
    </div>
  );
}
